import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { apiClient } from '../../apiClient';
import { API_ENDPOINTS } from '../../constants';
import { Coupon } from '../../types';
import { extractApiErrorMessage } from '../../utils/errorHandler';
import { formatCurrency } from '../../utils/pricing';

interface CouponForm {
  code: string;
  description: string;
  discountType: 'percent' | 'fixed';
  discountValue: string;
  minOrderValue: string;
  maxDiscount: string;
  validFrom: string;
  validUntil: string;
  usageLimit: string;
  perUserLimit: string;
  newCustomersOnly: boolean;
}

const emptyForm: CouponForm = {
  code: '',
  description: '',
  discountType: 'percent',
  discountValue: '',
  minOrderValue: '0',
  maxDiscount: '',
  validFrom: '',
  validUntil: '',
  usageLimit: '',
  perUserLimit: '',
  newCustomersOnly: false
};

const toDateInput = (value?: string | null) => (value ? value.slice(0, 10) : '');

const AdminCouponsPage: React.FC = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Coupon | null>(null);
  const [form, setForm] = useState<CouponForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(API_ENDPOINTS.COUPONS);
      setCoupons(response.data as Coupon[]);
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Failed to load coupons'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogError(null);
    setDialogOpen(true);
  };

  const openEdit = (coupon: Coupon) => {
    setEditing(coupon);
    setForm({
      code: coupon.code,
      description: coupon.description || '',
      discountType: coupon.discountType,
      discountValue: String(coupon.discountValue),
      minOrderValue: String(coupon.minOrderValue ?? 0),
      maxDiscount: coupon.maxDiscount ? String(coupon.maxDiscount) : '',
      validFrom: toDateInput(coupon.validFrom),
      validUntil: toDateInput(coupon.validUntil),
      usageLimit: coupon.usageLimit ? String(coupon.usageLimit) : '',
      perUserLimit: coupon.perUserLimit ? String(coupon.perUserLimit) : '',
      newCustomersOnly: Boolean(coupon.newCustomersOnly)
    });
    setDialogError(null);
    setDialogOpen(true);
  };

  const onField = (field: keyof CouponForm) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const save = async () => {
    setSaving(true);
    setDialogError(null);
    const payload: any = {
      description: form.description || '',
      discountType: form.discountType,
      discountValue: Number(form.discountValue),
      minOrderValue: Number(form.minOrderValue) || 0,
      maxDiscount: form.maxDiscount ? Number(form.maxDiscount) : null,
      validFrom: form.validFrom || null,
      validUntil: form.validUntil ? `${form.validUntil}T23:59:59.999Z` : null,
      usageLimit: form.usageLimit ? Number(form.usageLimit) : null,
      perUserLimit: form.perUserLimit ? Number(form.perUserLimit) : null,
      newCustomersOnly: form.newCustomersOnly
    };
    try {
      if (editing) {
        await apiClient.put(`${API_ENDPOINTS.COUPONS}/${editing._id}`, payload);
      } else {
        await apiClient.post(API_ENDPOINTS.COUPONS, { ...payload, code: form.code.toUpperCase().trim() });
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      setDialogError(extractApiErrorMessage(err, 'Failed to save coupon'));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (coupon: Coupon) => {
    try {
      await apiClient.put(`${API_ENDPOINTS.COUPONS}/${coupon._id}`, { isActive: !coupon.isActive });
      setCoupons((prev) =>
        prev.map((c) => (c._id === coupon._id ? { ...c, isActive: !coupon.isActive } : c))
      );
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Failed to update coupon'));
    }
  };

  const remove = async (coupon: Coupon) => {
    if (!window.confirm(`Delete coupon ${coupon.code}? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`${API_ENDPOINTS.COUPONS}/${coupon._id}`);
      setCoupons((prev) => prev.filter((c) => c._id !== coupon._id));
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Failed to delete coupon'));
    }
  };

  const describeValue = (coupon: Coupon) =>
    coupon.discountType === 'percent'
      ? `${coupon.discountValue}%${coupon.maxDiscount ? ` (max ${formatCurrency(coupon.maxDiscount)})` : ''}`
      : formatCurrency(coupon.discountValue);

  const describeWindow = (coupon: Coupon) => {
    if (!coupon.validFrom && !coupon.validUntil) return 'Always';
    const from = coupon.validFrom ? new Date(coupon.validFrom).toLocaleDateString() : '…';
    const until = coupon.validUntil ? new Date(coupon.validUntil).toLocaleDateString() : '…';
    return `${from} → ${until}`;
  };

  const isExpired = (coupon: Coupon) =>
    Boolean(coupon.validUntil && new Date(coupon.validUntil) < new Date());

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Coupons
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Discount codes customers can apply at checkout
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          New coupon
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ overflow: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Discount</TableCell>
              <TableCell>Min order</TableCell>
              <TableCell>Valid</TableCell>
              <TableCell>Used</TableCell>
              <TableCell>Active</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              [...Array(3)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(7)].map((__, j) => (
                    <TableCell key={j}>
                      <Skeleton />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : coupons.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ textAlign: 'center', py: 5 }}>
                  <Typography color="text.secondary">
                    No coupons yet. Create your first one — e.g. WELCOME10 for 10% off.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              coupons.map((coupon) => (
                <TableRow key={coupon._id} hover>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600, fontFamily: 'monospace' }}>{coupon.code}</Typography>
                    {coupon.description && (
                      <Typography variant="caption" color="text.secondary">
                        {coupon.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{describeValue(coupon)}</TableCell>
                  <TableCell>
                    {coupon.minOrderValue ? formatCurrency(coupon.minOrderValue) : '—'}
                  </TableCell>
                  <TableCell>
                    {describeWindow(coupon)}
                    {isExpired(coupon) && (
                      <Chip label="Expired" size="small" color="warning" sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    {coupon.usedCount}
                    {coupon.usageLimit ? ` / ${coupon.usageLimit}` : ''}
                  </TableCell>
                  <TableCell>
                    <Switch size="small" checked={coupon.isActive} onChange={() => toggleActive(coupon)} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(coupon)}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => remove(coupon)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? `Edit ${editing.code}` : 'New coupon'}</DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
            <TextField
              label="Code"
              value={form.code}
              onChange={(e) => setForm((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
              disabled={Boolean(editing)}
              helperText={editing ? 'Code cannot change after creation' : 'Letters/numbers, e.g. WELCOME10'}
            />
            <TextField select label="Type" value={form.discountType} onChange={onField('discountType')}>
              <MenuItem value="percent">Percent off</MenuItem>
              <MenuItem value="fixed">Fixed amount off (₹)</MenuItem>
            </TextField>
            <TextField
              label={form.discountType === 'percent' ? 'Percent (%)' : 'Amount (₹)'}
              type="number"
              value={form.discountValue}
              onChange={onField('discountValue')}
            />
            <TextField
              label="Max discount (₹, optional)"
              type="number"
              value={form.maxDiscount}
              onChange={onField('maxDiscount')}
              disabled={form.discountType === 'fixed'}
            />
            <TextField
              label="Min order value (₹)"
              type="number"
              value={form.minOrderValue}
              onChange={onField('minOrderValue')}
            />
            <TextField
              label="Description (optional)"
              value={form.description}
              onChange={onField('description')}
            />
            <TextField
              label="Valid from"
              type="date"
              value={form.validFrom}
              onChange={onField('validFrom')}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Valid until"
              type="date"
              value={form.validUntil}
              onChange={onField('validUntil')}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Total usage limit (optional)"
              type="number"
              value={form.usageLimit}
              onChange={onField('usageLimit')}
            />
            <TextField
              label="Per-customer limit (optional)"
              type="number"
              value={form.perUserLimit}
              onChange={onField('perUserLimit')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.newCustomersOnly}
                  onChange={(e) => setForm((prev) => ({ ...prev, newCustomersOnly: e.target.checked }))}
                />
              }
              label="New customers only (first order)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={saving || !form.discountValue || (!editing && !form.code.trim())}
          >
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Create coupon'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminCouponsPage;
